import {Component, EventEmitter, Input, Output} from "@angular/core";
import {AddressService} from "../address/address.service";
import {ContactService} from "../contact/contact.service";
import {OrderService} from "./order.service";
import {createEvent} from "../misc/event.polyfill";
import {SplitDeviceQuantityDialogComponent} from "./split-device-quantity-dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../misc/handle-errors.decorator";
import {
  OrderFormGqlModel,
  OrderFormLineItemModel,
  shippingDestinationMap,
} from "./order-form.gql-model";
import {GqlMicroFormMutationModelFactory, GqlMicroFormQueryModel} from "../graphql/graphql.model";
import gql from "graphql-tag";
import {DeviceOrderSplitResultModel, HardwareOrderDetailsInputModel} from "./order.gql-model";
import {DialogRejectOrderComponent} from "../workflow/order/dialog-reject-order.component";
import {WorkflowService} from "../workflow/workflow.service";
import {Router} from "@angular/router";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";
import {SelectStagingWarehouseDialogComponent} from "../workflow/order/select-staging-warehouse-dialog.component";

@Component({
  selector: "order-form",
  templateUrl: "./order-form.template.pug",
  providers: [OrderService, ConfirmDialogService],
})
@exposeComponentInDebugMode()
export class OrderFormComponent {
  shippingDestinationValues = shippingDestinationMap;

  editingOrderResults: boolean = false;
  originalOrderResults: any[] = [];

  bulkUpdateOrderNumber: string = "";
  bulkUpdateOrderDate: string = "";
  submittingOrder: boolean = false;
  activateEditMode = {
    requestedDeliveryDate: false,
    orderNotificationEmailList: false,
    deliveryInformation: false,
    activateEditMode: false,
    shippingDestination: false,
  };

  @Input()
  orderForm: OrderFormGqlModel = new OrderFormGqlModel({});

  @Input()
  submitOrder: any;

  leasingPartnerAddress = {
    US: {
      name: "HEWLETT-PACKARD FINANCIAL",
      street: "200 CONNELL DR FL 5",
      city: "BERKELEY HEIGHTS",
      region: "NEW JERSEY",
      cp: "07922-2805",
      hpfs: "900308963",
    },
    CA: {
      name: "HEWLETT PACKARD FINANCIAL",
      street: "5150 SPECTRUM WAY",
      city: "MISSISSAUGA",
      region: "ONTARIO",
      cp: "L4W 5G2",
      hpfs: "900512426",
    },
    GB: {
      name: "Hewlett Packard International BAnck DAC",
      street: "Liffey Park Technology Campus",
      city: "Leixlip, Country Kinldare",
      region: "Ireland",
      cp: "00000",
      hpfs: "900642710",
    },
    FR: {
      name: "Hewlett Packard International BAnck DAC",
      street: "Liffey Park Technology Campus",
      city: "Leixlip, Country Kinldare",
      region: "Ireland",
      cp: "00000",
      hpfs: "900642710",
    },
    DE: {
      name: "Hewlett Packard International BAnck DAC",
      street: "Liffey Park Technology Campus",
      city: "Leixlip, Country Kinldare",
      region: "Ireland",
      cp: "00000",
      hpfs: "900642710",
    },
    NL: {
      name: "Hewlett Packard International BAnck DAC",
      street: "Liffey Park Technology Campus",
      city: "Leixlip, Country Kinldare",
      region: "Ireland",
      cp: "00000",
      hpfs: "900674766",
    },
    ZA: {
      name: "HP Financial Services Holding Sandton Designated Activity Company",
      street: "76 Maude Street",
      city: "Johannesburg",
      region: "South Africa",
      cp: "2196",
      hpfs: "900674766",
    },
  };

  @Input()
  itsmAddress: any = {
    leasingPartner: {},
    endCustomer: {},
  };

  @Input()
  allowEditing: {[key: string]: boolean} = {};

  @Input()
  showReject: boolean;

  @Input()
  showCancel: boolean;

  @Output("error")
  errorEmitter: EventEmitter<Event> = new EventEmitter();

  orderMicroFormQuery: any = gql`
    ${GqlMicroFormQueryModel.fragment}

    mutation updateOrderFormField($id: ID!, $paramName: String!, $paramValue: JSON!) {
      result: hardwareOrderField(id: $id, paramName: $paramName, paramValue: $paramValue) {
        ...microFormData
      }
    }
  `;

  orderFieldMutators = [
    "shippingDestination",
    "deliveryMethod",
    "salValNumber",
    "shipCompleted",
    "floorLevel",
    "estimatedDeliveryDate",
    "estimatedHardwareOrderDate",
    "targetDeliveryDate",
    "extraPricingInformation",
    "resellerInformation",
    "resellerInstructions",
    "deliveryInformation",
    "orderNotificationEmailList",
    "sourceDocumentationLink",
    "requestedDeliveryDate",
  ];

  microFormMutators: {[key: string]: GqlMicroFormMutationModelFactory} = this.orderFieldMutators.reduce(
    (obj, key) => {
      obj[key] = new GqlMicroFormMutationModelFactory(key, () => this.orderForm.id, this.orderMicroFormQuery, "result");
      return obj;
    },
    {},
  );

  // noinspection JSUnusedLocalSymbols - it's actually used
  private set error(error: Error) {
    const event = createEvent("error");
    event["error"] = error;
    this.errorEmitter.emit(event);
  }

  constructor(
    private orderService: OrderService,
    private addressService: AddressService,
    private contactService: ContactService,
    private dialogService: DialogService,
    private workflowTargetInfo: WorkflowService,
    public router: Router,
    private confirmDialogService: ConfirmDialogService,
  ) { }

  forwardError(event): void {
    this.errorEmitter.emit(event);
  }

  @handleErrors()
  async editSiteAddress() {
    const {changed, address} = await this.addressService.modifyAddress("site", this.orderForm.siteId);

    if (changed) {
      this.orderForm.header.siteAddress = address;
    }
  }

  @handleErrors()
  async editInstallationContact() {
    const {changed, contact} = await this.contactService.modifyContact("installation", "order", this.orderForm.id);

    if (changed) {
      this.orderForm.header.installationContact = contact;
    }
  }

  @handleErrors()
  editShippingDestination() {
    SelectStagingWarehouseDialogComponent.showModal(this.dialogService, {
      selectedShippingDestination: this.orderForm.header.shippingDestination,
      title: "Select Shipping Destination",
    })
      .subscribe((selectedWarehouse) => {
        if (selectedWarehouse) {
          this.orderForm.header.shippingDestination = selectedWarehouse.shippingDestination;
        }
      });
  }

  @handleErrors()
  async editDeliveryContact() {
    const {changed, contact} = await this.contactService.modifyContact("delivery", "order", this.orderForm.id);

    if (changed) {
      this.orderForm.header.deliveryContact = contact;
    }
  }

  editOrderResults() {
    this.originalOrderResults = this.orderForm.devices.map(({orderNumber, orderDate}) => ({orderNumber, orderDate}));
    this.bulkUpdateOrderNumber = "";
    this.bulkUpdateOrderDate = "";
    this.editingOrderResults = true;
  }

  copyBulkOrderNumberAndDate() {
    if (this.bulkUpdateOrderNumber) {
      this.orderForm.devices.forEach((it) => { it.orderNumber = this.bulkUpdateOrderNumber; });
    }

    if (this.bulkUpdateOrderDate) {
      this.orderForm.devices.forEach((it) => { it.orderDate = this.bulkUpdateOrderDate; });
    }
  }

  @handleErrors()
  async rejectOrder() {
    const id = this.workflowTargetInfo.workflowConfig.targetId;

    const success = await DialogRejectOrderComponent.showRejectOrderDialogModal(this.dialogService, id);

    if (success) {
      await this.router.navigate(["/order"], {queryParams: {id}});
    }
  }

  @handleErrors()
  async remove() {
    const result = await this.confirmDialogService.confirm(
      "Cancel Order Request Confirmation",
      `Are you certain you want to Cancel Order Request?`,      
      "Dismiss",
      "Cancel Order Request",
    );

    if (result) {
      await this.orderService.remove(this.orderForm.id);
      await this.router.navigate(["/orders"], {queryParams: {siteId: this.orderForm.siteId}});
    }
  }

  @handleErrors()
  async onSubmit() {
    const result = await this.confirmDialogService.confirm(
      "Submit Order Confirmation",
      `Changes cannot be made after the order is submitted`,      
      "Dismiss",
      "Submit Order",
    );

    if (result) {
      this.submittingOrder = true;
      try {
        await this.orderService.submitOrderToItsm(this.orderForm.id);
        await this.submitOrder(this.orderForm.id);
      } finally {
        this.submittingOrder = false;
      }
    }
  }

  cancelOrderResults() {
    this.orderForm.devices.forEach((it, index) => {
      if (!this.originalOrderResults[index]) return;
      it.orderNumber = this.originalOrderResults[index].orderNumber;
      it.orderDate = this.originalOrderResults[index].orderDate;
    });
    this.editingOrderResults = false;
  }

  @handleErrors()
  async saveOrderResults() {
    const updates = this.orderForm.devices
      .filter(it => !it.indented && !it.multipleOrderNumbers)
      .map(it => new HardwareOrderDetailsInputModel(it));

    await this.orderService.updateOrderNumbersAndDates(this.orderForm.id, updates);

    this.editingOrderResults = false;
  }

  handleSplitResult(
    index: number,
    items: OrderFormLineItemModel[],
    result: DeviceOrderSplitResultModel[],
    isAutoSplit = false,
  ) {
    if (!result || !result.length) return;

    const replacements = [];
    const spliceCount = items.length;

    for (const {splitId, quantity, orderNumber, orderDate} of result) {
      for (const row of items) {
        const output = new OrderFormLineItemModel(row);
        output.splitId = splitId;
        output.orderQuantity = quantity;
        output.extendedPrice = ((+row.cataloguePrice) * (+quantity)).toString(10);

        if (isAutoSplit) {
          output.setOrderNumberAndDate(orderNumber, orderDate);
        }

        replacements.push(output);
      }
    }

    this.orderForm.devices.splice(index, spliceCount, ...replacements);

    let lastShaded = true;

    for (const device of this.orderForm.devices) {
      if (!device.indented) {
        lastShaded = !lastShaded;
      }

      device.shaded = lastShaded;
    }
  }

  getItemsForSplit(index) {
    const target = this.orderForm.devices[index];
    const items = [target];

    for (let i = index + 1; i < this.orderForm.devices.length; i++) {
      const row = this.orderForm.devices[i];

      if (row && row.indented) {
        items.push(row);
      } else {
        break;
      }
    }

    return {items, target};
  }

  @handleErrors()
  async splitDevice(index) {
    const {items, target} = this.getItemsForSplit(index);

    const data = {
      totalQuantity: +target.orderQuantity,
      deviceId: target.id,
      splitId: target.splitId,
      siteOrderId: this.orderForm.id,
    };

    const result: DeviceOrderSplitResultModel[] =
      await SplitDeviceQuantityDialogComponent.show(this.dialogService, data);

    this.handleSplitResult(index, items, result, false);
  }

  @handleErrors()
  async autoSplitDevice(index) {
    const {items, target} = this.getItemsForSplit(index);

    const result: DeviceOrderSplitResultModel[] =
      await this.orderService.autoSplitDevice(target.id, target.splitId, this.orderForm.id);

    this.handleSplitResult(index, items, result, true);
  }
}
